import { useRef } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";
import { REVIEWS } from "../config/site";

function SourceRating({ review, index }) {
  if (review.source === "Airbnb") {
    return (
      <span className="inline-flex items-center gap-0.5 shrink-0" data-testid={`review-stars-${index + 1}`}>
        {[...Array(5)].map((_, s) => (
          <Star key={s} size={11} className="fill-lavglow text-lavglow" />
        ))}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center font-mono text-[0.6rem] font-bold text-ink bg-lavglow rounded-full px-2.5 py-0.5"
      data-testid={`review-score-${index + 1}`}
    >
      {review.score}/10
    </span>
  );
}

export default function ReviewsWall() {
  const trackRef = useRef(null);

  const slide = (dir) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: "smooth" });
  };

  return (
    <section className="pb-24 md:pb-32 px-6 md:px-16" data-testid="reviews-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between gap-6 mb-12">
          <div>
            <Reveal><Eyebrow className="mb-5">Guest reviews</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="display-2 text-cream">
                Straight from the <span className="serif-italic text-lavglow">guest book.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="hidden md:flex items-center gap-3 shrink-0">
              <button
                onClick={() => slide(-1)}
                className="p-3 rounded-full border border-white/15 text-sage hover:text-lavglow hover:border-lavglow transition-colors"
                aria-label="Previous reviews"
                data-testid="reviews-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => slide(1)}
                className="p-3 rounded-full border border-white/15 text-sage hover:text-lavglow hover:border-lavglow transition-colors"
                aria-label="Next reviews"
                data-testid="reviews-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={trackRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 items-stretch"
            data-testid="reviews-track"
          >
            {REVIEWS.map((r, i) => (
              <figure
                key={`${r.name}-${i}`}
                className="min-w-[85%] sm:min-w-[calc(50%-10px)] lg:min-w-[calc((100%-40px)/3)] snap-start flex flex-col border border-white/10 rounded-3xl bg-moss/60 p-7 hover:border-lavglow/30 transition-colors"
                data-testid={`review-card-${i + 1}`}
              >
                <div className="flex items-center justify-between gap-3 mb-4">
                  <span className="font-mono text-[0.56rem] uppercase tracking-widest2 text-fern">
                    {r.dome || r.source}
                  </span>
                  <SourceRating review={r} index={i} />
                </div>
                <div className="flex-1">
                  {r.title && (
                    <p className="font-display text-xl text-cream leading-snug mb-3">“{r.title}”</p>
                  )}
                  <p className="text-sage text-sm leading-relaxed">{r.quote}</p>
                </div>
                <figcaption className="pt-5 mt-5 border-t border-white/10">
                  <p className="text-cream text-sm">
                    {r.name}
                    {r.country ? <span className="text-sage"> · {r.country}</span> : null}
                  </p>
                  <p className="text-sage text-xs mt-1">{r.stay}</p>
                  <p className="font-mono text-[0.56rem] uppercase tracking-widest2 text-fern mt-3">
                    Verified {r.source} review
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </Reveal>

        <div className="flex md:hidden items-center justify-center gap-3 mt-8">
          <button
            onClick={() => slide(-1)}
            className="p-3 rounded-full border border-white/15 text-sage active:text-lavglow active:border-lavglow"
            aria-label="Previous reviews"
            data-testid="reviews-prev-mobile"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            onClick={() => slide(1)}
            className="p-3 rounded-full border border-white/15 text-sage active:text-lavglow active:border-lavglow"
            aria-label="Next reviews"
            data-testid="reviews-next-mobile"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
